import type { PaymentMethodDetails } from './types/fsui';

type ReloadDataResult = [unknown, PaymentMethodDetails[] | undefined];

type PollForNewPaymentMethodOptions = {
  reloadData: () => Promise<ReloadDataResult>;
  previousCount: number;
  initialDelayMs?: number;
  intervalMs?: number;
  maxRetries?: number;
};

const DEFAULT_INITIAL_DELAY_MS = 1000;
const DEFAULT_INTERVAL_MS = 3000;
const DEFAULT_MAX_RETRIES = 10;

const wait = (ms: number): Promise<void> => new Promise((resolve) => setTimeout(resolve, ms));

export const pollForNewPaymentMethod = async ({
  reloadData,
  previousCount,
  initialDelayMs = DEFAULT_INITIAL_DELAY_MS,
  intervalMs = DEFAULT_INTERVAL_MS,
  maxRetries = DEFAULT_MAX_RETRIES,
}: PollForNewPaymentMethodOptions): Promise<ReloadDataResult> => {
  await wait(initialDelayMs);

  const attempt = async (retriesRemaining: number): Promise<ReloadDataResult> => {
    const result = await reloadData();
    const [, paymentMethods] = result;

    if ((paymentMethods?.length ?? 0) !== previousCount || retriesRemaining <= 0) {
      return result;
    }

    await wait(intervalMs);
    return attempt(retriesRemaining - 1);
  };

  return attempt(maxRetries);
};

type PollForNewlyCreatedPaymentMethodOptions = {
  reloadData: () => Promise<ReloadDataResult>;
  previousPaymentMethodIds: string[];
  initialDelayMs?: number;
  intervalMs?: number;
  maxRetries?: number;
};

// The reloaded list is [...activePaymentMethods, ...recentPaymentMethods] -- a newly-created method's
// position in that concatenation isn't guaranteed to be last, so it must be identified by id-diff
// against the ids present before creation, not by array position.
export const pollForNewlyCreatedPaymentMethod = async ({
  reloadData,
  previousPaymentMethodIds,
  ...timingOptions
}: PollForNewlyCreatedPaymentMethodOptions): Promise<PaymentMethodDetails | undefined> => {
  const [, paymentMethods] = await pollForNewPaymentMethod({
    reloadData,
    previousCount: previousPaymentMethodIds.length,
    ...timingOptions,
  });

  const previousIds = new Set(previousPaymentMethodIds);
  return paymentMethods?.find((method) => !previousIds.has(method.id));
};
